<template>
  <div>
    <div v-if="isSearchable" class="mb-2">
      <SearchBar
        inputId="contentSearchBar"
        :searchPlaceholder="`Search for a ${searchField}`"
        @onChange="onSearch"
      />
    </div>
    <CustomLabel
      v-if="inputLabel"
      :inputLabel="inputLabel"
      :inputId="inputId"
      :isMandatory="isMandatory"
    />
    <el-checkbox
      v-model="checkAllStatus"
      :indeterminate="isCheckAllIndeterminate"
      :disabled="isDisabled"
      @change="onCheckAllChange()"
      :size="isTabletScreen || isSmall ? 'small' : 'default'"
    >
      Select All
    </el-checkbox>
    <el-checkbox-group
      v-model="checkedValues"
      :disabled="isDisabled"
      :size="isTabletScreen || isSmall ? 'small' : 'default'"
    >
      <el-checkbox-button
        v-for="checkboxItem in processedCheckboxItems"
        v-show="checkboxItem.IsVisible"
        :key="checkboxItem.Id"
        :value="checkboxItem.Id"
        :label="checkboxItem.Name"
        :disabled="checkboxItem.IsDisabled"
        @change="onCheckedItemsChange"
        :class="checkboxItem.Name"
      />
    </el-checkbox-group>
    <div v-if="isFormField" class="form-group">
      <Field v-model="checkedValues" :id="inputId" :name="inputId" hidden />
      <ErrorMessage :name="inputId" class="validation-message" />
    </div>
  </div>
</template>

<script lang="ts">
import CustomLabel from "@/components/input/CustomLabel.vue";
import SearchBar from "@/components/input/SearchBar.vue";
import {
  handleCheckboxCheckAllChange,
  handleCheckboxSearch,
  isCheckboxCheckAll,
  isCheckboxCheckAllIndeterminate,
  setCheckboxItemsDisabled,
} from "@/methods/input";
import { deepCopyObject } from "@/methods/utils";
import { useSessionStore } from "@/stores/sessionStore";
import { IdNamePair } from "@/types/global";
import { storeToRefs } from "pinia";
import { ErrorMessage, Field } from "vee-validate";
import { PropType, defineComponent, onBeforeMount, ref } from "vue";

export default defineComponent({
  name: "CustomCheckbox",
  props: {
    inputId: { type: String, required: true },
    checkboxItems: { type: Array as PropType<Array<IdNamePair>> },
    initialValue: { type: Array as PropType<Array<number>>, default: [] },
    maxItems: { type: Number, default: undefined },
    isSearchable: { type: Boolean, default: false },
    searchField: { type: String, default: "value" },
    inputLabel: { type: String },
    isMandatory: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    isFormField: { type: Boolean, default: false },
    isSmall: { type: Boolean, default: false },
  },
  components: { SearchBar, CustomLabel, ErrorMessage, Field },
  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const { isTabletScreen } = storeToRefs(sessionStore);
    const initialValue = props.initialValue === null ? [] : props.initialValue;
    const checkedValues = ref(initialValue);
    const checkAllStatus = ref(false);
    const isCheckAllIndeterminate = ref(false);

    const processedCheckboxItems = ref<Array<IdNamePair>>();
    let totalCheckboxItems = props.checkboxItems ? props.checkboxItems.length : 0;

    onBeforeMount(() => {
      processedCheckboxItems.value = props.checkboxItems
        ? (deepCopyObject(props.checkboxItems) as Array<IdNamePair>)
        : [];
      // This is required to init IsVisible = true for all items on load
      handleCheckboxSearch(processedCheckboxItems.value, "");
      setCheckboxItemsDisabled(processedCheckboxItems.value, checkedValues.value, props.maxItems);
    });

    function onCheckedItemsChange() {
      checkAllStatus.value = isCheckboxCheckAll(
        processedCheckboxItems.value,
        checkedValues.value,
        totalCheckboxItems,
      );
      isCheckAllIndeterminate.value = isCheckboxCheckAllIndeterminate(
        processedCheckboxItems.value,
        checkedValues.value,
        totalCheckboxItems,
      );
      setCheckboxItemsDisabled(processedCheckboxItems.value, checkedValues.value, props.maxItems);
      emit("onChange", checkedValues.value);
    }
    function onCheckAllChange() {
      const updatedValues = handleCheckboxCheckAllChange(
        processedCheckboxItems.value,
        checkedValues.value,
        checkAllStatus.value,
        totalCheckboxItems,
        props.maxItems,
      );
      checkedValues.value = updatedValues.checkedValues;
      isCheckAllIndeterminate.value = updatedValues.isCheckAllIndeterminate;
      setCheckboxItemsDisabled(processedCheckboxItems.value, checkedValues.value, props.maxItems);
      emit("onChange", checkedValues.value);
    }
    function onSearch(searchedValue: string) {
      handleCheckboxSearch(processedCheckboxItems.value, searchedValue);
    }
    return {
      isTabletScreen,
      processedCheckboxItems,
      checkedValues,
      checkAllStatus,
      isCheckAllIndeterminate,
      onCheckedItemsChange,
      onCheckAllChange,
      onSearch,
    };
  },
});
</script>

<style lang="scss">
.el-checkbox-button {
  & .el-checkbox-button__inner {
    border: 1px solid $color-gray !important;
    border-radius: 3px;
    margin-right: 3px;
    margin-bottom: 3px;
  }
  &.is-checked .el-checkbox-button__inner {
    border-color: $color-info !important;
  }
}
</style>
